/* import __COLOCATED_TEMPLATE__ from './inserter.hbs'; */
/* RESPONSIBLE TEAM: team-proactive-support */
import { action } from '@ember/object';

import Component from '@glimmer/component';
import type Graph from '../../../models/graph-editor/graph';
import type Node from '../../../models/graph-editor/node';
import Offset from '../../../models/graph-editor/offset';
import Coordinates from '../../../models/graph-editor/coordinates';
import BLANK_IMAGE from '../../../data/blank-image';
import type ConnectionPointInputs from '../../../models/graph-editor/connection-point';

interface Args {
  graph: Graph;
  connectionPoints: Array<ConnectionPointInputs>;
  dataObject: any;
  onDrag?(node: Node): void;
  onDrop?(node: Node): void;
}

export default class GraphEditorNodeInserterComponent extends Component<Args> {
  blankDragImage: HTMLImageElement = new Image(1, 1);

  constructor(owner: unknown, args: Args) {
    super(owner, args);
    this.args = args;
    this.blankDragImage.src = BLANK_IMAGE;
  }

  @action onDragStart(event: DragEvent): void {
    this.args.graph.trackActions(() => {
      let coordinates = Coordinates.scaledAndRounded(event.clientX, event.clientY, this.args.graph);

      // Use the Graph Hooks API to determine if we can insert this new Node.
      if (this.args.graph.hooks.canInsertNode(this.args.dataObject)) {
        //Deselect any currently selected nodes
        this.args.graph.state.selectedNodes.clear();

        let node = this.args.graph.addNode({
          position: { x: coordinates.x, y: coordinates.y },
          connectionPoints: this.args.connectionPoints,
          dataObject: this.args.dataObject,
        });

        this.args.graph.hooks.didInsertNode(node);

        if (this.args.onDrag) {
          this.args.onDrag(node);
        }

        // Set a reference to the dragging node
        this.args.graph.state.draggingNode = node;

        // Set the cursor offset, this allows us to keep the element 'snapped' in the right position
        this.args.graph.state.cursorOffset = new Offset(event.offsetX, event.offsetY);

        // Capture what the initial the positioning coordinates are.
        // We will move all selected nodes relative to this point.
        this.args.graph.state.positioningCoordinates = coordinates;

        // Dragging automatically selects the node
        this.args.graph.state.addSelectedNode(node);
      } else {
        event.preventDefault();
        event.stopPropagation();
      }

      // Firefox requires data to be set for the element to start dragging
      event.dataTransfer?.setData('text/plain', '');

      // Set the data image to be blank so the browser doesn't default
      // to the 'ghost image' effect while dragging.
      event.dataTransfer?.setDragImage(this.blankDragImage, 1, 1);
    });
  }

  @action onDragEnd(_: DragEvent): void {
    if (this.args.onDrop) {
      this.args.onDrop(this.args.graph.state.draggingNode!);
    }
    this.args.graph.state.draggingNode = undefined;
  }
}
